/**
 * Cartographer's pinned development origin.
 *
 * The AppHost pins `cartographer-app` to 50967 because its Entra SPA redirect
 * URI is registered for that port, so in the dev server this is where
 * Cartographer actually is. It is never used in a build — a production bundle
 * pointing at localhost would be a link that works on exactly one machine.
 */
const CARTOGRAPHER_DEV_ORIGIN = 'http://localhost:50967'

/**
 * A path the server may hand us, and nothing else: no scheme, no host, no `//`,
 * and no query string — hand-off paths are bare Cartographer routes.
 */
const SAFE_PATH = /^\/(?!\/)[A-Za-z0-9/_-]*$/

/** The configured origin when it is a real absolute http(s) URL, else null. */
function configuredOrigin(): string | null {
  // Read statically, at call time: static so Vite substitutes this one key and
  // nothing else (conventions.test.ts), call time so vi.stubEnv reaches it.
  const value = import.meta.env.VITE_CARTOGRAPHER_URL?.trim()
  if (!value) return null
  // A variable missing from the ADO variable group arrives as the literal text
  // "$(VITE_CARTOGRAPHER_URL)", not as nothing — so it must parse, or it would
  // become a relative, dead link.
  let parsed: URL
  try {
    parsed = new URL(value)
  } catch {
    return null
  }
  return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? value : null
}

/**
 * An absolute Cartographer URL for a bare route, or null when this build does
 * not know where Cartographer is (or the path is not a bare route).
 *
 * Null is rendered as a NAMED destination, never as a link: the path is
 * relative to CARTOGRAPHER, and as a bare `href` it would resolve against this
 * app's own origin — a route Observatory does not have, so a dead link dressed
 * as a hand-off. A fake affordance is worse than none (doc 00). See
 * `components/cartographer/CartographerLink.tsx`, which renders both forms.
 *
 * Born as `townCrierUrl` in `components/schedules/scheduleFormat.ts` and moved
 * here when governance, the report registry and retirement became its second,
 * third and fourth callers; `townCrierUrl` now delegates.
 */
export function cartographerUrl(path: string): string | null {
  if (!SAFE_PATH.test(path)) return null
  const base = configuredOrigin() ?? (import.meta.env.DEV ? CARTOGRAPHER_DEV_ORIGIN : null)
  if (base === null) return null
  return `${base.replace(/\/+$/, '')}${path}`
}

/**
 * One report's own page in Cartographer — `/reports/{id}` (Cartographer W3-1, decision Q-15), which opens the report in
 * Spyglass after sign-in. A hand-off about ONE report lands on it, rather than on a list the reader must search.
 *
 * Cartographer answers a report the reader may not open exactly as one that does not exist, so a link from here
 * discloses nothing Observatory did not already show. An id that is not a bare route segment fails SAFE_PATH above and
 * is rendered as a named destination, like every other unlinked hand-off.
 */
export function cartographerReportPath(reportId: string): string {
  return `/reports/${encodeURIComponent(reportId)}`
}
